import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { alarmsApi } from '../services/api';
import { formatBSDateTime, formatBSDateTimeShort } from '../utils/bs';
import { NepaliDateTimePicker } from '../components/NepaliDatePicker';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import type { AlarmSeverity, AlarmStatus, AlarmStats, PaginatedResponse, Alarm, AlarmType } from '../types';

const SEVERITY_CLASS: Record<AlarmSeverity, string> = {
  critical: 'badge-crit',
  warning:  'badge-warn',
  info:     'badge-info',
};

const STATUS_CLASS: Record<AlarmStatus, string> = {
  active:       'badge-crit',
  acknowledged: 'badge-warn',
  resolved:     'badge-ok',
};

const CAT_ICON: Record<string, string> = {
  electrical:  '⚡',
  mechanical:  '⚙️',
  hydraulic:   '💧',
  operational: '🏭',
};

// ── Log Alarm modal ───────────────────────────────────────────────────────────

function LogAlarmModal({ alarmTypes, onClose }: { alarmTypes: AlarmType[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    alarm_type_id: '',
    severity:      '',
    message:       '',
    reported_at:   new Date().toISOString().slice(0, 16),
    notes:         '',
  });

  const selectedType = alarmTypes.find(t => t.id === Number(form.alarm_type_id));

  const mut = useMutation({
    mutationFn: () => alarmsApi.create({
      alarm_type_id: Number(form.alarm_type_id),
      severity:      form.severity || undefined,
      message:       form.message || undefined,
      reported_at:   form.reported_at ? new Date(form.reported_at).toISOString() : undefined,
      notes:         form.notes || undefined,
    }),
    onSuccess: () => {
      toast.success('Alarm logged');
      qc.invalidateQueries({ queryKey: ['alarms'] });
      qc.invalidateQueries({ queryKey: ['alarm-stats'] });
      onClose();
    },
    onError: () => toast.error('Failed to log alarm'),
  });

  // Group alarm types by category
  const byCategory: Record<string, AlarmType[]> = {};
  alarmTypes.forEach(t => { (byCategory[t.category] ??= []).push(t); });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hydro-border">
          <h2 className="text-base font-bold text-slate-900">Log Alarm</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">×</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Alarm type selector */}
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Alarm Type <span className="text-red-500">*</span></label>
            <select
              className="input w-full"
              value={form.alarm_type_id}
              onChange={e => {
                const t = alarmTypes.find(a => a.id === Number(e.target.value));
                setForm(p => ({
                  ...p,
                  alarm_type_id: e.target.value,
                  severity: t?.default_severity ?? '',
                  message: t?.name ?? '',
                }));
              }}
            >
              <option value="">— Select alarm type —</option>
              {Object.entries(byCategory).map(([cat, types]) => (
                <optgroup key={cat} label={`${CAT_ICON[cat] ?? '•'} ${cat.charAt(0).toUpperCase() + cat.slice(1)}`}>
                  {types.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {selectedType?.description && (
              <p className="text-xs text-slate-400 mt-1">{selectedType.description}</p>
            )}
          </div>

          {/* When it occurred */}
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">When Occurred <span className="text-red-500">*</span></label>
            <NepaliDateTimePicker
              value={form.reported_at}
              onChange={v => setForm(p => ({ ...p, reported_at: v }))}
            />
          </div>

          {/* Severity (pre-filled from default, editable) */}
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Severity</label>
            <select className="input w-full" value={form.severity} onChange={e => setForm(p => ({...p, severity: e.target.value}))}>
              <option value="">— Use default —</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </div>

          {/* Custom message */}
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Message</label>
            <input
              className="input w-full"
              value={form.message}
              onChange={e => setForm(p => ({...p, message: e.target.value}))}
              placeholder="Describe what happened…"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Notes</label>
            <textarea
              className="input w-full h-20 resize-none"
              value={form.notes}
              onChange={e => setForm(p => ({...p, notes: e.target.value}))}
              placeholder="Additional details, readings, context…"
            />
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-hydro-border">
          <button
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !form.alarm_type_id}
            className="btn-primary flex-1"
          >
            {mut.isPending ? 'Logging…' : 'Log Alarm'}
          </button>
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Acknowledge modal ─────────────────────────────────────────────────────────

function AckModal({ alarm, onClose }: { alarm: Alarm; onClose: () => void }) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => alarmsApi.acknowledge(alarm.id, notes || undefined),
    onSuccess: () => {
      toast.success('Acknowledged');
      qc.invalidateQueries({ queryKey: ['alarms'] });
      qc.invalidateQueries({ queryKey: ['alarm-stats'] });
      onClose();
    },
    onError: () => toast.error('Failed to acknowledge'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hydro-border">
          <h2 className="text-base font-bold text-slate-900">Acknowledge Alarm</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">×</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Alarm summary */}
          <div className="rounded-lg bg-slate-50 border border-hydro-border px-4 py-3">
            <p className="text-sm font-semibold text-slate-900">{alarm.alarm_type_name ?? alarm.alarm_type}</p>
            <p className="text-xs text-slate-500 mt-0.5">{alarm.message}</p>
            <p className="text-xs text-slate-400 mt-1">
              {formatBSDateTime(new Date(alarm.reported_at ?? alarm.triggered_at))}
            </p>
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Acknowledgment Notes</label>
            <textarea
              autoFocus
              className="input w-full h-24 resize-none"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Actions taken, investigation notes, remediation steps…"
            />
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-hydro-border">
          <button onClick={() => mut.mutate()} disabled={mut.isPending} className="btn-primary flex-1">
            {mut.isPending ? 'Acknowledging…' : 'Acknowledge'}
          </button>
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Alarm detail row expansion ────────────────────────────────────────────────

function AlarmRow({ alarm, canAck, onAck }: {
  alarm: Alarm; canAck: boolean; onAck: (a: Alarm) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="table-row text-sm cursor-pointer hover:bg-slate-50/70"
        onClick={() => setExpanded(e => !e)}
      >
        <td className="px-4 py-3 text-xs font-mono text-slate-500 whitespace-nowrap">
          {formatBSDateTimeShort(new Date(alarm.reported_at ?? alarm.triggered_at))}
        </td>
        <td className="px-4 py-3">
          <p className="text-xs font-semibold text-slate-800">{alarm.alarm_type_name ?? alarm.alarm_type}</p>
          {alarm.alarm_category && (
            <p className="text-xs text-slate-400">{CAT_ICON[alarm.alarm_category]} {alarm.alarm_category}</p>
          )}
        </td>
        <td className="px-4 py-3">
          <span className={SEVERITY_CLASS[alarm.severity] ?? 'badge-info'}>{alarm.severity}</span>
        </td>
        <td className="px-4 py-3">
          <span className={STATUS_CLASS[alarm.status] ?? 'badge-info'}>{alarm.status}</span>
        </td>
        <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate">{alarm.message}</td>
        <td className="px-4 py-3 text-xs text-slate-400">
          {alarm.reported_by_name ?? '—'}
        </td>
        {canAck && (
          <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
            {alarm.status === 'active' && (
              <button
                onClick={() => onAck(alarm)}
                className="text-xs px-2 py-1 rounded bg-brand-700 hover:bg-brand-600 text-white"
              >ACK</button>
            )}
          </td>
        )}
      </tr>
      {expanded && (
        <tr className="bg-slate-50/60">
          <td colSpan={canAck ? 7 : 6} className="px-6 py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <p className="text-slate-400 mb-0.5">Reported at</p>
                <p className="text-slate-700">
                  {alarm.reported_at ? formatBSDateTime(new Date(alarm.reported_at)) : '—'}
                </p>
              </div>
              <div>
                <p className="text-slate-400 mb-0.5">Reported by</p>
                <p className="text-slate-700">{alarm.reported_by_name ?? 'System'}</p>
              </div>
              {alarm.acknowledged_at && (
                <div>
                  <p className="text-slate-400 mb-0.5">Acknowledged</p>
                  <p className="text-slate-700">
                    {formatBSDateTime(new Date(alarm.acknowledged_at))}
                    {alarm.acknowledged_by_name && <> · {alarm.acknowledged_by_name}</>}
                  </p>
                </div>
              )}
              {alarm.acknowledge_notes && (
                <div className="col-span-2">
                  <p className="text-slate-400 mb-0.5">Ack Notes</p>
                  <p className="text-slate-700">{alarm.acknowledge_notes}</p>
                </div>
              )}
              {alarm.notes && (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-slate-400 mb-0.5">Notes</p>
                  <p className="text-slate-700">{alarm.notes}</p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main Alarms page ──────────────────────────────────────────────────────────

interface AlarmFilters { status: string; severity: string; }

export default function Alarms() {
  const qc = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canAck = hasPermission('alarms');

  const [filters,   setFilters]   = useState<AlarmFilters>({ status: '', severity: '' });
  const [page,      setPage]      = useState(0);
  const [logOpen,   setLogOpen]   = useState(false);
  const [ackAlarm,  setAckAlarm]  = useState<Alarm | null>(null);
  const LIMIT = 25;

  const { data, isLoading } = useQuery<PaginatedResponse<Alarm>>({
    queryKey: ['alarms', filters, page],
    queryFn: () => alarmsApi.list({ ...filters, limit: LIMIT, offset: page * LIMIT }).then(r => r.data),
    placeholderData: prev => prev,
  });

  const { data: stats } = useQuery<AlarmStats>({
    queryKey: ['alarm-stats'],
    queryFn: () => alarmsApi.stats().then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: typesData } = useQuery({
    queryKey: ['alarm-types'],
    queryFn: () => alarmsApi.listTypes().then(r => r.data),
  });

  const alarms     = data?.data ?? [];
  const total      = data?.total ?? 0;
  const alarmTypes = typesData?.alarm_types ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Alarm Management</h1>
        {canAck && (
          <button onClick={() => setLogOpen(true)} className="btn-primary text-sm">
            + Log Alarm
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label:'Active',          value: stats.active_count,   cls:'text-red-600' },
            { label:'Critical Active', value: stats.critical_count, cls:'text-red-700' },
            { label:'Last 24h',        value: stats.last_24h,       cls:'text-amber-600' },
            { label:'Last 7 Days',     value: stats.last_7d,        cls:'text-blue-600' },
          ].map(s => (
            <div key={s.label} className="card text-center">
              <p className={`text-3xl font-bold font-mono ${s.cls}`}>{s.value ?? '—'}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card flex flex-wrap gap-4 items-center">
        <select value={filters.status} onChange={e => { setFilters(f => ({...f, status: e.target.value})); setPage(0); }} className="input w-40">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="resolved">Resolved</option>
        </select>
        <select value={filters.severity} onChange={e => { setFilters(f => ({...f, severity: e.target.value})); setPage(0); }} className="input w-40">
          <option value="">All Severity</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>
        <button onClick={() => { setFilters({ status:'', severity:'' }); setPage(0); }} className="btn-ghost text-sm">Clear</button>
        <span className="ml-auto text-sm text-slate-500">{total} records</span>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hydro-border text-xs text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Time</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Severity</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Message</th>
                <th className="px-4 py-3 text-left">Reported By</th>
                {canAck && <th className="px-4 py-3 text-left">Action</th>}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="table-row">
                    <td colSpan={canAck ? 7 : 6} className="px-4 py-3">
                      <div className="h-4 bg-slate-200 rounded animate-pulse w-3/4" />
                    </td>
                  </tr>
                ))
              ) : alarms.length === 0 ? (
                <tr>
                  <td colSpan={canAck ? 7 : 6} className="px-4 py-8 text-center text-slate-400">No alarms found</td>
                </tr>
              ) : alarms.map(a => (
                <AlarmRow key={a.id} alarm={a} canAck={canAck} onAck={setAckAlarm} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-hydro-border text-xs text-slate-500">
          <span>{Math.min(page * LIMIT + 1, total)}–{Math.min((page+1)*LIMIT, total)} of {total}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(0,p-1))} disabled={page === 0} className="btn-ghost py-1 px-2 text-xs disabled:opacity-40">← Prev</button>
            <button onClick={() => setPage(p => p+1)} disabled={(page+1)*LIMIT >= total} className="btn-ghost py-1 px-2 text-xs disabled:opacity-40">Next →</button>
          </div>
        </div>
      </div>

      {logOpen  && <LogAlarmModal alarmTypes={alarmTypes} onClose={() => setLogOpen(false)} />}
      {ackAlarm && <AckModal alarm={ackAlarm} onClose={() => setAckAlarm(null)} />}
    </div>
  );
}
